/**
 * The two halves of the opening: the front (daylight, paper, flat UI) and
 * the back (night, a terminal, racks, a database).
 *
 * Both are drawn on an 800 × 900 canvas and anchored to the seam (xMax and
 * xMin, sliced): whatever the screen ratio, what sits next to the middle
 * stays on screen.
 *
 * @module scenes/opening
 */

import { brandPath } from '../components/brands.js';
import { circle, rect, scene } from './svg.js';

/**
 * A brand mark drawn in a scene, centred on (x, y).
 *
 * @param {string} name - A key of components/brands.js.
 * @param {number} x
 * @param {number} y
 * @param {number} scale - 1 draws it 24 units wide.
 * @param {string} className - A palette class.
 * @returns {string}
 */
function mark(name, x, y, scale, className) {
    return `<path d="${brandPath(name)}" class="${className}" transform="translate(${x} ${y}) scale(${scale}) translate(-12 -12)"/>`;
}

/** @returns {import('../lib/html.js').Markup} */
export function front() {
    const columns = [160, 320, 480, 640].map((x) => `<line x1="${x}" y1="0" x2="${x}" y2="900"/>`).join('');

    const browserWindow =
        '<g transform="translate(130 300)">' +
        rect(0, 0, 520, 330, 'fill-paper-2', 'rx="10"') +
        rect(0, 0, 520, 40, 'fill-red', 'rx="10"') +
        rect(0, 20, 520, 20, 'fill-red') +
        circle(24, 20, 6, 'fill-paper') +
        circle(44, 20, 6, 'fill-paper') +
        circle(64, 20, 6, 'fill-paper') +
        rect(36, 80, 250, 28, 'fill-ink') +
        rect(36, 126, 300, 10, 'fill-line') +
        rect(36, 148, 250, 10, 'fill-line') +
        rect(36, 170, 280, 10, 'fill-line') +
        rect(36, 212, 150, 46, 'fill-red', 'rx="23"') +
        rect(36, 282, 26, 26, 'fill-red') +
        rect(70, 282, 26, 26, 'fill-ink') +
        rect(104, 282, 26, 26, 'fill-rose') +
        rect(138, 282, 26, 26, 'fill-line') +
        // The swatches end on the design tool, clear of the pointer.
        mark('figma', 214, 295, 1.08, 'fill-ink') +
        rect(360, 80, 124, 100, 'fill-line') +
        circle(452, 108, 14, 'fill-red') +
        '<path d="M360 180 L408 128 L440 160 L456 146 L484 180 Z" class="fill-ink"/>' +
        '</g>';

    const typeSpecimen =
        '<g transform="translate(130 672)">' +
        rect(0, 0, 150, 130, 'fill-paper-2', 'rx="10"') +
        '<text x="22" y="92" class="type-display fill-ink" font-size="66">Aa</text>' +
        '</g>' +
        '<g transform="translate(306 708)">' +
        rect(0, 0, 100, 50, 'fill-red', 'rx="25"') +
        circle(75, 25, 18, 'fill-paper') +
        '</g>';

    const pointer = '<path d="M300 545 L300 590 L311 579 L320 598 L327 595 L318 576 L333 576 Z" class="fill-ink"/>';

    const phone =
        '<g transform="translate(560 520)">' +
        rect(0, 0, 150, 290, 'fill-ink', 'rx="24"') +
        rect(10, 14, 130, 262, 'fill-paper', 'rx="16"') +
        rect(10, 14, 130, 44, 'fill-red', 'rx="16"') +
        rect(10, 40, 130, 18, 'fill-red') +
        rect(22, 74, 106, 56, 'fill-paper-2', 'rx="6"') +
        rect(22, 140, 106, 56, 'fill-paper-2', 'rx="6"') +
        rect(22, 206, 70, 10, 'fill-line') +
        '</g>';

    return scene(
        '0 0 800 900',
        rect(0, 0, 800, 900, 'fill-paper') +
            `<g class="stroke-ink" stroke-width="1" opacity=".07">${columns}</g>` +
            browserWindow +
            typeSpecimen +
            pointer +
            phone +
            rect(0, 840, 800, 60, 'fill-paper-2'),
        'xMaxYMid slice',
    );
}

/** @returns {import('../lib/html.js').Markup} */
export function back() {
    const stars = [
        [90, 80, 1.6],
        [260, 140, 1.2],
        [520, 70, 1.8],
        [690, 190, 1.2],
        [410, 230, 1.4],
        [740, 40, 1.2],
        [180, 250, 1],
        [620, 120, 1.4],
        [330, 60, 1],
    ]
        .map(([cx, cy, r]) => circle(cx, cy, r))
        .join('');

    const terminalWindow =
        '<g transform="translate(150 300)">' +
        rect(0, 0, 520, 330, 'fill-night-2', 'rx="10"') +
        rect(0, 0, 520, 40, 'fill-night-3', 'rx="10"') +
        rect(0, 20, 520, 20, 'fill-night-3') +
        circle(24, 20, 6, 'fill-dim') +
        circle(44, 20, 6, 'fill-dim') +
        circle(64, 20, 6, 'fill-dim') +
        '<g class="type-mono" font-size="19">' +
        '<text x="30" y="92" class="fill-dim">$ bin/phpunit</text>' +
        '<text x="30" y="124" class="fill-amber">OK (3,358 tests)</text>' +
        '<text x="30" y="172" class="fill-dim">$ curl /api/work_orders</text>' +
        '<text x="30" y="204" class="fill-amber">HTTP/2 200</text>' +
        '<text x="30" y="252" class="fill-dim">$ mercure publish /jobs</text>' +
        rect(30, 272, 11, 22, 'fill-amber scene__caret') +
        '</g>' +
        '</g>';

    let units = '';
    let leds = '';

    for (let unit = 0; unit < 4; unit++) {
        const y = 27 + unit * 36;
        units += rect(12, 14 + unit * 36, 166, 26, 'fill-night-3');
        leds +=
            circle(30, y, 4, `${unit === 2 ? 'fill-red' : 'fill-amber'} scene__led`) +
            circle(44, y, 4, `${unit === 0 ? 'fill-red' : 'fill-amber'} scene__led`);
    }

    // The containers live in the rack: a Docker sticker on its top unit.
    const docker = mark('docker', 150, 27, 1.3, 'fill-amber');
    const rack = `<g transform="translate(110 690)">${rect(0, 0, 190, 230, 'fill-night-2')}${units}${leds}${docker}</g>`;

    // A stack of three disks: only the front edge of each joint shows. The
    // PostgreSQL elephant sits in the middle of the middle disk, which runs
    // from y 64 to y 108 under its centre (scaled from its 24 × 24 box).
    const database =
        '<g transform="translate(430 700)">' +
        rect(0, 0, 160, 130, 'fill-night-2') +
        '<ellipse cx="80" cy="130" rx="80" ry="20" class="fill-night-2"/>' +
        '<path d="M0 44 A80 20 0 0 0 160 44" class="stroke-night-3" stroke-width="3"/>' +
        '<path d="M0 88 A80 20 0 0 0 160 88" class="stroke-night-3" stroke-width="3"/>' +
        '<ellipse cx="80" cy="0" rx="80" ry="20" class="fill-night-3"/>' +
        mark('postgresql', 80, 86, 1.2, 'fill-amber') +
        '</g>';

    const cables =
        '<path d="M200 690 C 200 640, 260 660, 280 630" class="stroke-amber" stroke-width="2" opacity=".55"/>' +
        '<path d="M520 700 C 560 660, 600 680, 610 630" class="stroke-amber" stroke-width="2" opacity=".55"/>';

    return scene(
        '0 0 800 900',
        rect(0, 0, 800, 900, 'fill-night') +
            `<g class="fill-dim" opacity=".5">${stars}</g>` +
            terminalWindow +
            cables +
            rack +
            database +
            rect(0, 840, 800, 60, 'fill-night-2'),
        'xMinYMid slice',
    );
}
